import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ClipboardCheck,
  Plus,
  Edit,
  Eye,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import type { QATestSuite, QASuiteJSON } from '@/types/qaTypes';

const defaultSuiteJson: QASuiteJSON = {
  tests: [
    {
      test_id: 'example-001',
      name: 'Example test',
      category: 'refusal',
      conversation: {
        steps: [{ role: 'user', content: 'Sample question?' }],
      },
      auto_checks: {
        fail_if_regex: [],
        pass_if_contains_any: ['example', 'help'],
      },
    },
  ],
  pass_criteria: {
    min_pass_rate: 0.85,
    hard_fail_categories: ['pii', 'refusal'],
  },
};

const AdminQASuites: React.FC = () => {
  const [suites, setSuites] = useState<QATestSuite[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedSuite, setSelectedSuite] = useState<QATestSuite | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formSuiteId, setFormSuiteId] = useState('');
  const [formVersion, setFormVersion] = useState('1.0.0');
  const [formIndustry, setFormIndustry] = useState('');
  const [formPurpose, setFormPurpose] = useState('');
  const [formJson, setFormJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    fetchSuites();
  }, []);

  const fetchSuites = async () => {
    try {
      const { data, error } = await supabase
        .from('qa_test_suites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const parsedSuites = (data || []).map(s => ({
        ...s,
        suite_json: s.suite_json as unknown as QATestSuite['suite_json'],
      })) as QATestSuite[];
      setSuites(parsedSuites);
    } catch (error) {
      console.error('Error fetching suites:', error);
      toast({
        title: 'Error loading QA suites',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (suite: QATestSuite) => {
    try {
      const { error } = await supabase
        .from('qa_test_suites')
        .update({ enabled: !suite.enabled })
        .eq('id', suite.id);

      if (error) throw error;

      setSuites(prev =>
        prev.map(s => (s.id === suite.id ? { ...s, enabled: !s.enabled } : s))
      );

      toast({
        title: suite.enabled ? 'Suite disabled' : 'Suite enabled',
      });
    } catch (error) {
      console.error('Error toggling suite:', error);
      toast({
        title: 'Error updating suite',
        variant: 'destructive',
      });
    }
  };

  const openEditor = (suite?: QATestSuite) => {
    if (suite) {
      setSelectedSuite(suite);
      setFormSuiteId(suite.suite_id);
      setFormVersion(suite.suite_version);
      setFormIndustry(suite.industry);
      setFormPurpose(suite.purpose);
      setFormJson(JSON.stringify(suite.suite_json, null, 2));
    } else {
      setSelectedSuite(null);
      setFormSuiteId('');
      setFormVersion('1.0.0');
      setFormIndustry('');
      setFormPurpose('');
      setFormJson(JSON.stringify(defaultSuiteJson, null, 2));
    }
    setJsonError(null);
    setEditorOpen(true);
  };

  const validateJson = (jsonStr: string): QASuiteJSON | null => {
    try {
      const parsed = JSON.parse(jsonStr);
      
      // Basic validation
      if (!parsed.tests || !Array.isArray(parsed.tests)) {
        setJsonError('suite_json must contain a "tests" array');
        return null;
      }
      if (!parsed.pass_criteria) {
        setJsonError('suite_json must contain "pass_criteria"');
        return null;
      }
      if (typeof parsed.pass_criteria.min_pass_rate !== 'number') {
        setJsonError('pass_criteria.min_pass_rate must be a number');
        return null;
      }
      
      // Validate each test
      for (const test of parsed.tests) {
        if (!test.test_id || !test.name || !test.category) {
          setJsonError('Each test must have test_id, name, and category');
          return null;
        }
        if (!test.conversation?.steps || !Array.isArray(test.conversation.steps)) {
          setJsonError(`Test "${test.test_id}" must have conversation.steps array`);
          return null;
        }
      }
      
      setJsonError(null);
      return parsed as QASuiteJSON;
    } catch (e) {
      setJsonError('Invalid JSON: ' + (e as Error).message);
      return null;
    }
  };

  const handleSave = async () => {
    if (!formSuiteId || !formVersion || !formIndustry || !formPurpose) {
      toast({
        title: 'All fields are required',
        variant: 'destructive',
      });
      return;
    }

    const parsedJson = validateJson(formJson);
    if (!parsedJson) return;

    setSaving(true);

    try {
      if (selectedSuite) {
        // Update existing
        const { error } = await supabase
          .from('qa_test_suites')
          .update({
            suite_id: formSuiteId,
            suite_version: formVersion,
            industry: formIndustry,
            purpose: formPurpose,
            suite_json: JSON.parse(JSON.stringify(parsedJson)),
          })
          .eq('id', selectedSuite.id);

        if (error) throw error;
        toast({ title: 'Suite updated' });
      } else {
        // Create new
        const { error } = await supabase.from('qa_test_suites').insert([{
          suite_id: formSuiteId,
          suite_version: formVersion,
          industry: formIndustry,
          purpose: formPurpose,
          suite_json: JSON.parse(JSON.stringify(parsedJson)),
        }]);

        if (error) throw error;
        toast({ title: 'Suite created' });
      }

      setEditorOpen(false);
      fetchSuites();
    } catch (error: any) {
      console.error('Error saving suite:', error);
      toast({
        title: 'Error saving suite',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const openViewer = (suite: QATestSuite) => {
    setSelectedSuite(suite);
    setViewerOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6" />
              QA Test Suites
            </h1>
            <p className="text-muted-foreground">
              Manage machine-readable QA test definitions
            </p>
          </div>
          <Button onClick={() => openEditor()}>
            <Plus className="w-4 h-4 mr-2" />
            New Suite
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">All Suites</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : suites.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No QA suites yet. Create one to get started.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Suite ID</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Tests</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suites.map(suite => (
                    <TableRow key={suite.id}>
                      <TableCell className="font-medium">{suite.suite_id}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{suite.suite_version}</Badge>
                      </TableCell>
                      <TableCell>{suite.industry}</TableCell>
                      <TableCell>{suite.purpose}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {suite.suite_json?.tests?.length || 0} tests
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={suite.enabled}
                          onCheckedChange={() => handleToggleEnabled(suite)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openViewer(suite)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditor(suite)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedSuite ? 'Edit QA Suite' : 'Create QA Suite'}
            </DialogTitle>
            <DialogDescription>
              Define test cases and pass criteria for automated QA testing
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="suiteId">Suite ID</Label>
                <Input
                  id="suiteId"
                  value={formSuiteId}
                  onChange={e => setFormSuiteId(e.target.value)}
                  placeholder="accounting-client-assistant"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  value={formVersion}
                  onChange={e => setFormVersion(e.target.value)}
                  placeholder="1.0.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  value={formIndustry}
                  onChange={e => setFormIndustry(e.target.value)}
                  placeholder="accounting"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purpose">Purpose</Label>
                <Input
                  id="purpose"
                  value={formPurpose}
                  onChange={e => setFormPurpose(e.target.value)}
                  placeholder="client_assistant"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="suiteJson">
                Suite JSON
                {jsonError && (
                  <span className="text-destructive ml-2 text-xs font-normal">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    {jsonError}
                  </span>
                )}
              </Label>
              <Textarea
                id="suiteJson"
                value={formJson}
                onChange={e => {
                  setFormJson(e.target.value);
                  validateJson(e.target.value);
                }}
                className="font-mono text-sm min-h-[400px]"
                placeholder='{"tests": [...], "pass_criteria": {...}}'
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !!jsonError}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Suite'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedSuite?.suite_id} v{selectedSuite?.suite_version}
            </DialogTitle>
            <DialogDescription>
              {selectedSuite?.industry} / {selectedSuite?.purpose}
            </DialogDescription>
          </DialogHeader>

          {selectedSuite && (
            <div className="space-y-4 py-4">
              <div>
                <h4 className="font-medium mb-2">Pass Criteria</h4>
                <div className="flex gap-4 text-sm">
                  <Badge variant="outline">
                    Min pass rate: {(selectedSuite.suite_json.pass_criteria.min_pass_rate * 100).toFixed(0)}%
                  </Badge>
                  <Badge variant="outline">
                    Hard fail categories: {selectedSuite.suite_json.pass_criteria.hard_fail_categories.join(', ')}
                  </Badge>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">
                  Tests ({selectedSuite.suite_json.tests.length})
                </h4>
                <div className="space-y-3">
                  {selectedSuite.suite_json.tests.map(test => (
                    <Card key={test.test_id} className="p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{test.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {test.test_id} • {test.category}
                          </p>
                        </div>
                        <Badge variant="secondary">{test.category}</Badge>
                      </div>
                      <div className="mt-2 text-sm bg-muted p-2 rounded">
                        <p className="text-muted-foreground text-xs mb-1">User prompt:</p>
                        {test.conversation.steps
                          .filter(s => s.role === 'user')
                          .map((s, i) => (
                            <p key={i}>"{s.content}"</p>
                          ))}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        {test.auto_checks.fail_if_regex?.length ? (
                          <span className="text-destructive mr-2">
                            ✕ Fail if: {test.auto_checks.fail_if_regex.join(', ')}
                          </span>
                        ) : null}
                        {test.auto_checks.pass_if_contains_any?.length ? (
                          <span className="text-green-600">
                            ✓ Pass if contains: {test.auto_checks.pass_if_contains_any.join(', ')}
                          </span>
                        ) : null}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewerOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setViewerOpen(false);
              if (selectedSuite) openEditor(selectedSuite);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Suite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminQASuites;
